import * as React from "react";
import { chunk } from "lodash";
import * as moment from "moment";
// # exceptions
import { ApiRequestException, MissingDependencyException } from "@/exceptions";
// # interfaces
import { iPlansApiService } from "@/interfaces/services/api/plans";
import { iPlan } from "@/interfaces/plans";
import { iSubscriptionService } from "@/interfaces/services/subscription";
import { iAuthService } from "@/services/auth";
import { iPromptChildProps } from "@/interfaces/ui";
// # components
import { Button } from "@/components/content";
import { Message, Spinner } from "@/components/feedback";
import { PlanPreview } from "@/components/plans";
// # hooks
import { useIsBusy } from "@/hooks";
// # services
import {
  auth as authService,
  subscriptionService,
  uiService,
} from "@/services";
import { plansService } from "@/services/api";
import { ManagePaymentCards, iPaymentCard } from "@/components/payment";
import { iSubscription } from "@/interfaces/subscriptions";
import SiteSettings from "@/services/siteSettings";
import axios, { AxiosResponse } from "axios";

declare var Stripe: any;

interface iProps {
  /**
   * Service used for fetching the authenticated user.
   */
  authService: iAuthService;
  /**
   * Service used for fetching plans from the API.
   */
  plansService?: iPlansApiService;
  /**
   * Service used for fetching the users' active subscription;
   */
  subscriptionService?: iSubscriptionService;
}

// Define an interface to represent the structure of your data
interface PaymentIntent {
  id: string;
  object: string;
  // ... other properties
  next_action: {
    redirect_to_url: {
      return_url: string;
      url: string;
    };
    type: string;
  };
  // ... other properties
}

interface PaymentIntentApiResponse {
  data: PaymentIntent; // Assuming your PaymentIntent type is defined elsewhere
}

/**
 * Prompt content displayed when the user is choosing a payment card.
 */
const ChooseCardPromptContent: React.SFC<iPromptChildProps> = ({
  setPromptValue,
}) => {
  const [selectedCard, setSelectedCard] = React.useState<null | iPaymentCard>(
    null,
  );

  const handleSelectedCard = (card) => {
    setSelectedCard(card);
    setPromptValue(card);
  };

  return (
    <>
      <ManagePaymentCards
        onCardSelected={handleSelectedCard}
        selectedCard={selectedCard}
      />
    </>
  );
};

/**
 * Prompt content displayed when the user is changing their subscription plan.
 */
const ChangePlanPromptContent: React.SFC<
  iPromptChildProps & { plan: iPlan }
> = ({ setPromptValue, plan }) => {
  setPromptValue(true);

  return (
    <Message theme="warning">
      Are you sure you want to change your subscription plan to {plan.name}?
      <br />
      <br />
      Your plan will be updated immediately, and you will be changed{" "}
      {SiteSettings.get("currency_symbol")}
      {`${(plan.price / 100).toFixed(2)}`} per month on your renewal date.
    </Message>
  );
};

/**
 * Prompt content displayed when the user is changing their subscription plan.
 */
const ThreeDSecurePromptContent: React.SFC<
  iPromptChildProps & { paymentIntent: PaymentIntent }
> = ({ setPromptValue, paymentIntent }) => {
  React.useEffect(() => {
    setPromptValue(true);

    var iframe = document.createElement("iframe");
    iframe.src = paymentIntent.next_action.redirect_to_url.url;
    iframe.width = "100%";
    iframe.height = "100%";
    const input = document.getElementById(
      "threeDScreen",
    ) as HTMLInputElement | null;

    input.style.height = "520px";

    input.appendChild(iframe);
  }, [setPromptValue, paymentIntent]);

  return <div id="threeDScreen"></div>;
};

/**
 * Prompt content displayed when the user cancelling their subscription.
 */
const CancelPlanPromptContent: React.SFC<iPromptChildProps> = ({
  setPromptValue,
}) => {
  setPromptValue(true);

  return (
    <Message
      theme="warning"
      title="Are you sure you want to cancel your subscription?"
    >
      You will continue to have access to the benfits provided by your current
      plan until your billing cycle ends.
      <br />
      <br />
      Your card will no longer be charged.
    </Message>
  );
};

/**
 * Renders a list of subscriptions.
 * Allows the user to create, change, and cancel their plan.
 */
export const AccountSubscriptionScene: React.SFC<iProps> = (props: iProps) => {
  if (!props.authService) {
    throw new MissingDependencyException(
      "authService",
      "<AccountSubscriptionScene>",
    );
  }
  if (!props.plansService) {
    throw new MissingDependencyException(
      "plansService",
      "<AccountSubscriptionScene>",
    );
  }
  if (!props.subscriptionService) {
    throw new MissingDependencyException(
      "subscriptionService",
      "<AccountSubscriptionScene>",
    );
  }

  // Hooks: state.
  const [subscription, setSubscription] = React.useState<null | iSubscription>(
    null,
  );
  const [activePlan, setActivePlan] = React.useState<null | iPlan>(null);
  const [plans, setPlans] = React.useState<iPlan[]>([]);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [successMessage, setSuccessMessage] = React.useState<null | string>(
    null,
  );
  const [isPlanBusy, setIsPlanBusy] = React.useState<false | iPlan>(false);
  // Hooks: promise resolvers.
  const plansResolver = React.useMemo(
    () => props.plansService.get({ active: 1 }),
    [],
  );
  const activeSubscriptionResolver = React.useMemo(
    () => props.subscriptionService.get(),
    [],
  );
  const isLoading = useIsBusy([plansResolver, activeSubscriptionResolver]);
  // Hooks: handle promise results.
  React.useEffect(() => {
    plansResolver.then((response) => {
      setPlans(response.data.map((plan) => plan.data));
    });
    activeSubscriptionResolver.then((subscription) => {
      if (!subscription) {
        return;
      }
      setSubscription(subscription);
      setActivePlan(subscription.plan);
    });
  }, []);

  const isActivePlan = (plan: iPlan): boolean =>
    activePlan && activePlan.id === plan.id;

  const isPlanLoading = (plan: iPlan): boolean =>
    isPlanBusy && isPlanBusy.id === plan.id;

  /**
   * Subscribes the user to a plan.
   * Prompts the user to choose a payment method for new subscriptions.
   *
   * @param {iPlan} plan
   * @returns {Promise<void>}
   */
  const purchaseSubscription = async (plan: iPlan): Promise<void> => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      setIsPlanBusy(plan);

      if (activePlan) {
        try {
          await uiService.prompt(
            (promptProps) => (
              <ChangePlanPromptContent {...promptProps} plan={plan} />
            ),
            {
              title: "Confirm Plan Change",
            },
          );
        } catch (err) {
          setIsPlanBusy(false);
          return;
        }
        await props.subscriptionService.changePlan(plan.id);
      } else {
        let card: iPaymentCard;

        try {
          // Ask the user to choose/add a card.
          card = await uiService.prompt<iPaymentCard>(
            (promptProps) => <ChooseCardPromptContent {...promptProps} />,
            {
              title: "Payment Details",
            },
          );
        } catch (err) {
          setIsPlanBusy(false);
          return;
        }

        // Create subscription.
        try {
          await props.subscriptionService.create(plan.id, card.stripe_id);
        } catch (err) {
          if (err.statusCode === 428) {
            //do a call
            try {
              const response = await axios.post<PaymentIntentApiResponse>(
                "/api/subscription/threedsecure",
                {
                  id: err.errorResponseMessage,
                },
              );

              const paymentIntent: PaymentIntent = response.data.data;
              //check to see if we have a redirect url, if we dont then skip

              if (paymentIntent.next_action != null) {
                try {
                  await uiService.prompt(
                    (promptProps) => (
                      <ThreeDSecurePromptContent
                        {...promptProps}
                        paymentIntent={paymentIntent}
                      />
                    ),
                    {
                      title: "Confirm 3D Secure",
                    },
                  );
                } catch (promptError) {
                  // Handle errors related to the 3D Secure prompt
                  console.error(
                    "Error displaying 3D Secure prompt:",
                    promptError,
                  );
                }
              }
            } catch (error) {
              // Handle errors related to the axios request
              console.error("Error fetching payment intent:", error);

              // Optionally, you might want to set some state or perform additional error handling here
            } finally {
              setIsPlanBusy(false);
            }
          } else {
            //throw err;
          }
        }
      }

      setActivePlan(plan);
      setIsPlanBusy(false);
      setSuccessMessage(`Your plan has been changed to ${plan.name}`);
    } catch (err) {
      if (err.code) {
        console.log(err.code);
      }

      /**
       * Catch timeouts and presume that the request was successful.
       * @todo this is temporary! Remove this once the API timeout issue has been fixed.
       */
      if (err instanceof ApiRequestException && err.isTimedOut()) {
        setIsPlanBusy(false);
        setSuccessMessage(
          "Your subscription has been changed. Please refresh the page to see your updated plan.",
        );
        return;
      }

      setIsPlanBusy(false);
      setErrorMessage(err);
      throw err;
    }
  };

  /**
   * Cancels the users' subscription
   *
   * @returns {Promise<void>}
   */
  const cancelSubscription = async (): Promise<void> => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      setIsPlanBusy(activePlan);

      try {
        await uiService.prompt(
          (promptProps) => <CancelPlanPromptContent {...promptProps} />,
          { title: "Confirm Cancellation" },
        );
      } catch (err) {
        setIsPlanBusy(false);
        return;
      }
      await props.subscriptionService.cancel();
      setIsPlanBusy(false);
      setSuccessMessage(
        "Your subscription has been cancelled. Please refresh the page to see your updated plan.",
      );
    } catch (err) {
      /**
       * Catch timeouts and presume that the request was successful.
       * @todo this is temporary! Remove this once the API timeout issue has been fixed.
       */
      if (err instanceof ApiRequestException && err.isTimedOut()) {
        setIsPlanBusy(false);
        setSuccessMessage(
          "Your subscription has been cancelled. Please refresh the page to see your updated plan.",
        );
        return;
      }

      setErrorMessage(err);
      setIsPlanBusy(false);
      throw err;
    }
  };

  /**
   * Renders the correct button options displayed underneath a plan.
   *
   * @param {iPlan} plan
   * @returns {React.ReactNode}
   */
  const renderPlanControls = (plan: iPlan): React.ReactNode => {
    const loading: boolean = isPlanLoading(plan);
    const activeButtonLabel: string = activePlan ? "Change" : "Purchase";

    if (isActivePlan(plan)) {
      if (subscription && subscription.ends_at) {
        return (
          <Message theme="warning">
            Your subscription will end on{" "}
            {moment(subscription.ends_at).format("Do MMMM Y")}
          </Message>
        );
      }

      return (
        <Button
          disabled={!!isPlanBusy}
          loading={loading}
          onClick={cancelSubscription}
          theme="lowlight"
        >
          {loading ? "Updating" : "Cancel Subscription"}
        </Button>
      );
    }

    if (
      activePlan != null &&
      ((activePlan.period === "month" && plan.period === "year") ||
        activePlan.period === "year")
    ) {
      return null;
    }

    return (
      <Button
        className="purchasebutton"
        disabled={!!isPlanBusy}
        loading={loading}
        onClick={() => purchaseSubscription(plan)}
      >
        {loading ? "Updating" : activeButtonLabel}
      </Button>
    );
  };

  // Break plans into groups of three per row
  const rows = chunk(plans, 4);

  return (
    <div className="scene account-subscription">
      {successMessage ? (
        <Message theme="success" title="Subscription Updated">
          {successMessage}
        </Message>
      ) : null}

      {errorMessage ? (
        <Message
          theme="danger"
          title="There was a problem updating your subscription"
        >
          {errorMessage.message ||
            "Unknown error, please try again or contact support."}
        </Message>
      ) : null}

      {isLoading ? <Spinner /> : null}

      {!isLoading && !plans.length ? (
        <Message theme="info">No plans available.</Message>
      ) : null}

      {rows.map((row, rowKey) => (
        <div className="subbox" key={rowKey}>
          {row.map((plan, planKey) => (
            <PlanPreview
              active={isActivePlan(plan)}
              disabled={isPlanBusy && isPlanBusy.id !== plan.id}
              plan={plan}
              key={planKey}
            >
              {renderPlanControls(plan)}
            </PlanPreview>
          ))}
        </div>
      ))}
    </div>
  );
};

AccountSubscriptionScene.displayName = "AccountSubscriptionScene";
AccountSubscriptionScene.defaultProps = {
  authService,
  plansService,
  subscriptionService,
};
